import { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { cleanSupplierDetails, getSupplierDetails, updateSupplierField, updateSupplier, createSupplier } from "redux/slices/Suppliers";
import { showToast } from "redux/slices/Toasts";
import { RootState } from "redux/store";
import { Title, FormInput, Spinner, Modal } from "components";
import { SupplierNameMappingGrid } from "./SupplierNameMappings/SupplierNameMappingGrid";
import { getSupplierNameMappingDetails, cleanSupplierNameMappingDetails } from "redux/slices/SupplierNameMappings";

export function NewSupplier() {
	const form = useRef(null);
	const navigate = useNavigate();
	const { supplierId } = useParams();
	const dispatch = useAppDispatch();
	const [isValidated, setValidated] = useState<boolean>(false);
	const supplier = useAppSelector((state: RootState) => state.suppliers);
	const [isNewSupplier, setIsNewSupplier] = useState<boolean>(true);
	const [showConfirm, setShowConfirm] = useState<boolean>(false);

	useEffect(() => {
		setValidated(false);
		if (supplierId) {
			const id = parseInt(supplierId);
			dispatch(getSupplierDetails(id));
			dispatch(getSupplierNameMappingDetails(parseInt(supplierId)));
			setIsNewSupplier(false);
		} else {
			cleanSupplierData();
			setIsNewSupplier(true);
		}
	}, [supplierId]);

	function handleSubmit() {
		setShowConfirm(false);
		dispatch(updateSupplierField({ key: "loading", value: true }));
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			const supplierDetails = {
				id: supplier.id,
				isSystem: supplier.isSystem,
				name: supplier.name,
				active: supplier.active,
				contactName: supplier.contactName,
				address: supplier.address,
				suburb: supplier.suburb,
				state: supplier.state,
				postcode: supplier.postcode,
				phoneNumber: supplier.phoneNumber,
				faxNumber: supplier.faxNumber,
				lock: supplier.lock,
				modelId: supplier.modelId,
				systemCode: supplier.systemCode,
			}

			if (isNewSupplier) {
				dispatch(createSupplier(supplierDetails)).then((res) => {
					handleUpdate();
					dispatch(updateSupplierField({ key: "loading", value: false }));
				});
			} else {
				dispatch(updateSupplier(supplierDetails)).then((res) => {
					handleUpdate();
					dispatch(updateSupplierField({ key: "loading", value: false }));
				});
			}
		} else {
			dispatch(updateSupplierField({ key: "loading", value: false }));
		}
		setValidated(true);
	}

	function handleUpdate() {
		setTimeout(() => {
			dispatch(updateSupplierField({ key: "loading", value: false }));
			navigate("/app/suppliers");
		}, 500);
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateSupplierField({ key: option, value: optionValue }));
	}

	function cleanSupplierData() {
		dispatch(cleanSupplierDetails())
		dispatch(cleanSupplierNameMappingDetails())
	}

	return (
		<>
			<Title pageTitle={isNewSupplier ? "New Supplier" : "Update Supplier"} crumbs={[{ name: "Suppliers", path: "/app/suppliers" }]} />
			<Row>
				<Col xs={12}>
					<Card>
						<Modal
							title="Confirm"
							show={showConfirm}
							buttonType="primary"
							buttonText={"Continue"}
							buttonCloseText="Cancel"
							handleSubmit={handleSubmit}
							handleClose={() => setShowConfirm(false)}
							description={
								<>
									<p>You are about to sync the supplier down to all stores.</p>
									<p>Are you sure you want to continue?</p>
								</>
							}
						/>
						<Card.Body>
							<Row className="mb-1">
								<Col xs={6}>
									<Form onSubmit={handleSubmit} validated={isValidated} ref={form} 
										// onKeyDown={(e) => {
										// 	if (e.key === "Enter") {
										// 		document.getElementById("submit-button")?.click();
										// 	}
										// }}
									>
										<Row>
											<h4 className="mt-2">Supplier Details</h4>
											<Col md={12}>
												<FormInput
													required
													label="Name"
													type="text"
													name="supplier-name"
													placeholder="Enter supplier name"
													containerClass={"mb-3"}
													value={supplier.name}
													disabled={supplier.isSystem}
													onChange={(e) => handleChange("name", e.target.value)}
												/>
											</Col>
										</Row>
										<Row>
											<Col md={12}>
												<FormInput
													label="Address"
													type="text"
													name="supplier-address"
													placeholder="Enter supplier address"
													containerClass={"mb-3"}
													value={supplier.address ?? ""}
													onChange={(e) => handleChange("address", e.target.value)}
												/>
											</Col>
										</Row>
										<Row>
											<Col md={12}>
												<FormInput
													label="Suburb"
													type="text"
													name="supplier-suburb"
													placeholder="Enter supplier suburb"
													containerClass={"mb-3"}
													value={supplier.suburb ?? ""}
													onChange={(e) => handleChange("suburb", e.target.value)}
												/>
											</Col>

										</Row>
										<Row>
											<Col md={12}>
												<Form.Group className="mb-3">
													<Form.Label className="form-check-label">State</Form.Label>
													<Form.Select
														className="supplier-state"
														onChange={(e) => handleChange("state", e.target.value)}
														value={supplier.state ?? undefined}
													>
														<option hidden value={undefined}>Choose a state</option>
														<option value="NSW" >NSW</option>
														<option value="VIC" >VIC</option>
														<option value="QLD" >QLD</option>
														<option value="SA" >SA</option>
														<option value="WA" >WA</option>
														<option value="TAS" >TAS</option>
														<option value="NT" >NT</option>
														<option value="ACT" >ACT</option>
													</Form.Select>
												</Form.Group>
											</Col>
											<Col md={12}>
												<FormInput
													label="Postcode"
													type="number"
													name="supplier-postcode"
													placeholder="Enter supplier postcode"
													containerClass={"mb-3"}
													max={9999}
													value={supplier.postcode ?? ""}
													maxLength={4}
													onChange={(e) => {
														if (e.target.value.length > 4) return;
														handleChange("postcode", e.target.value)
													}}
												/>
											</Col>
										</Row>
										<Row>
											<Col md={12}>
												<FormInput
													label="Phone Number"
													type="text"
													name="supplier-address"
													placeholder="Enter supplier phone number"
													containerClass={"mb-3"}
													value={supplier.phoneNumber ?? ""}
													key="text"
													onChange={(e) => handleChange("phoneNumber", e.target.value)}
												/>
											</Col>
										</Row>
										<Row>
											<Col md={12}>
												<FormInput
													label="Fax Number"
													type="text"
													name="supplier-fax-number"
													placeholder="Enter supplier fax number"
													containerClass={"mb-3"}
													value={supplier.faxNumber ?? ""}
													key="text"
													onChange={(e) => handleChange("faxNumber", e.target.value)}
												/>
											</Col>
										</Row>
										<Row>
											<Col md={12}>
												<Form.Group>
													<Form.Label className="form-check-label">Active</Form.Label>
													<Form.Check
														type="switch"
														id="active"
														checked={supplier.active}
														onChange={() => handleChange("active", !supplier.active)}
													/>
												</Form.Group>
											</Col>
										</Row>
										<Row className="mt-3">
											<Col sm={6}>
												<Link to="/app/suppliers" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold" onClick={() => cleanSupplierData()}>
													<i className="mdi mdi-arrow-left"></i> Back to All Suppliers {" "}
												</Link>
											</Col>
											<Col sm={6} className="d-flex flex-row-reverse">
												<Button id="submit-button" variant="primary" className="btn cta-button" disabled={supplier.loading} onClick={() => setShowConfirm(true)}>
													{supplier.loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
													{supplier.loading ? <>Saving...</> : <>Save</>}
												</Button>
											</Col>
										</Row>
									</Form>
								</Col>
								<Col xs={6}>
									<Row style={{marginTop: "2rem"}} >
										<Col md={12}>
											{isNewSupplier ? <div></div> : <><SupplierNameMappingGrid supplierId={supplier.id} /></>}
										</Col>
									</Row>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}